import { CostCode, useAddCostCodeMutation, useUpdateCostCodeMutation } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import Action from '@/gf/components/Action'
import CloseModalButton from '@/gf/components/CloseModalButton'
import Modal from '@/gf/components/ModalNext'
import Field from '@/gf/components/next/forms/Field'
import TextInput from '@/gf/components/next/forms/TextInput'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import { ApolloErrorWithMessages, Maybe, ModalSize } from '@/types'
import { useForm } from 'react-hook-form'
import { useRef } from 'react'

const EditCostCodeModal = ({
  open,
  onClose,
  costCode,
}: {
  open: boolean
  onClose: () => void
  costCode: Maybe<CostCode>
}) => {
  const client = useGqlClient()
  const [_, msgs] = useMsgs()
  const [performing, performingToggle] = useToggle()
  const [addCostCode] = useAddCostCodeMutation({ client })
  const [updateCostCode] = useUpdateCostCodeMutation({ client })
  const codeInputRef = useRef<HTMLInputElement | null>(null)
  const form = useForm({
    defaultValues: {
      code: costCode?.code ?? '',
      name: costCode?.name ?? '',
    },
  })

  const onSubmit = async ({ code, name }) => {
    try {
      performingToggle.on()
      if (!costCode) {
        await addCostCode({
          variables: {
            code,
            name,
          },
        })

        msgs.add('Cost Code Created!', 'positive')
      } else {
        await updateCostCode({
          variables: {
            id: costCode.id,
            code,
            name,
          },
        })

        msgs.add('Cost Code Updated!', 'positive')
      }

      onClose()
      client.reFetchObservableQueries()
    } catch (err) {
      console.error(err)
      const serverErrors =
        (err as ApolloErrorWithMessages)?.graphQLErrors
          .map((gqlErr) => gqlErr.fields.map((f) => f.value).flat())
          .flat() ?? []

      if (serverErrors.length > 0) {
        msgs.add(serverErrors[0], 'negative')
      }

      console.log(serverErrors)
    } finally {
      performingToggle.off()
    }
  }

  // Merge react-hook-form's ref with our own so the Modal can focus the Code
  // input on open (see `initialFocus` below).
  const { ref: registerCodeRef, ...codeField } = form.register('code', {
    required: 'Code is required',
  })
  const setCodeRef = (el: HTMLInputElement | null) => {
    registerCodeRef(el)
    codeInputRef.current = el
  }

  return (
    <Modal open={open} onClose={onClose} size={ModalSize.SM} initialFocus={codeInputRef}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="relative p-6 flex flex-col gap-y-4">
        <CloseModalButton onClick={onClose} className="absolute top-3 right-3" />

        <h3 className="text-2xl font-medium">
          {costCode ? `Edit Cost Code ${costCode.code}` : 'Add Cost Code'}
        </h3>
        <Field label="Code" error={form.formState.errors.code?.message}>
          <TextInput ref={setCodeRef} {...codeField} />
        </Field>

        <Field label="Name" error={form.formState.errors.name?.message}>
          <TextInput {...form.register('name', { required: 'Name is required' })} />
        </Field>

        <div className="flex gap-x-4 pt-4">
          <Action.S onClick={onClose} className="flex-1">
            Cancel
          </Action.S>
          <Action.P type="submit" className="flex-1" color="blue" performing={performing}>
            Save
          </Action.P>
        </div>
      </form>
    </Modal>
  )
}

export default EditCostCodeModal
